import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Search, ListChecks, MessageCircle, Mail, ChevronDown, ChevronUp, Star, Clock } from 'lucide-react';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { useNotifications } from '../../hooks/useNotifications';

type Outcome = 'vip' | 'qualified' | 'nurture';
type LeadStatus = 'new' | 'contacted' | 'converted' | 'lost';

interface QuizLead {
  _id: string;
  landingPage?: { internalName: string; slug: string } | null;
  landingPageSlugSnapshot: string;
  name: string;
  whatsapp: string;
  email: string;
  lang: string;
  answers: { questionKey: string; questionText: string; optionLabel: string }[];
  outcome: Outcome;
  status: LeadStatus;
  sdrDueAt?: string | null;
  notes: string;
  utm?: { source?: string; campaign?: string };
  nurture?: { step: number; completedAt?: string | null; unsubscribedAt?: string | null; lastSentAt?: string | null };
  createdAt: string;
}

const OUTCOME_BADGE: Record<Outcome, { label: string; cls: string }> = {
  vip: { label: 'VIP', cls: 'bg-amber-100 text-amber-800' },
  qualified: { label: 'Qualificado', cls: 'bg-green-100 text-green-800' },
  nurture: { label: 'Nutrição', cls: 'bg-blue-100 text-blue-800' }
};

const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'Novo',
  contacted: 'Contatado',
  converted: 'Convertido',
  lost: 'Perdido'
};

const formatDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// "vence em 5 h" / "atrasado há 3 h" — só para leads vip/qualificado ainda não contatados.
function sdrDeadline(lead: QuizLead): { text: string; overdue: boolean } | null {
  if (lead.outcome === 'nurture' || lead.status !== 'new' || !lead.sdrDueAt) return null;
  const diffH = Math.round((new Date(lead.sdrDueAt).getTime() - Date.now()) / 3600000);
  if (diffH < 0) return { text: `Atrasado há ${Math.abs(diffH)} h`, overdue: true };
  return { text: diffH === 0 ? 'Vence agora' : `Vence em ${diffH} h`, overdue: false };
}

function nurtureLabel(lead: QuizLead) {
  if (lead.outcome !== 'nurture' || !lead.nurture) return null;
  if (lead.nurture.unsubscribedAt) return 'Cancelou a inscrição';
  if (lead.nurture.completedAt) return `Sequência concluída (${lead.nurture.step} e-mail(s))`;
  return `${lead.nurture.step} e-mail(s) enviado(s)`;
}

const AdminLandingQuizLeads: React.FC = () => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState<QuizLead[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});

  const outcome = searchParams.get('outcome') || '';
  const status = searchParams.get('status') || '';
  const overdue = searchParams.get('overdue') === '1';

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: '100',
        ...(searchTerm && { search: searchTerm }),
        ...(outcome && { outcome }),
        ...(status && { status }),
        ...(overdue && { overdue: '1' })
      });
      const response = await axios.get(`/landing-pages/quiz-leads?${params}`);
      setLeads(response.data.leads);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Erro ao carregar leads do quiz:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, outcome, status, overdue]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const updateLead = async (lead: QuizLead, patch: Partial<Pick<QuizLead, 'status' | 'notes'>>) => {
    try {
      const res = await axios.put(`/landing-pages/quiz-leads/${lead._id}`, patch);
      setLeads((prev) => prev.map((l) => (l._id === lead._id ? res.data : l)));
      notifications.showSuccess('Lead atualizado', patch.status ? `Status: ${STATUS_LABELS[patch.status]}` : 'Notas salvas.');
    } catch (error: any) {
      notifications.showError('Erro ao atualizar', error.response?.data?.message || 'Tente novamente.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{t('admin.quizLeads')}</h1>
          <p className="text-gray-600">{t('admin.quizLeadsDesc')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow mb-6 p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Nome, e-mail ou WhatsApp"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <select value={outcome} onChange={(e) => setFilter('outcome', e.target.value)} className="lg:w-44 px-3 py-2 border border-gray-300 rounded-lg">
              <option value="">Todos os resultados</option>
              <option value="vip">VIP</option>
              <option value="qualified">Qualificado</option>
              <option value="nurture">Nutrição</option>
            </select>
            <select value={status} onChange={(e) => setFilter('status', e.target.value)} className="lg:w-40 px-3 py-2 border border-gray-300 rounded-lg">
              <option value="">Todos os status</option>
              {(Object.keys(STATUS_LABELS) as LeadStatus[]).map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
              <input type="checkbox" checked={overdue} onChange={(e) => setFilter('overdue', e.target.checked ? '1' : '')} className="h-4 w-4 rounded border-gray-300 text-primary-600" />
              Só atrasados (+24 h)
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-3">{total} lead(s)</p>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : leads.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <ListChecks className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900">Nenhum lead encontrado</h3>
              <p className="text-sm text-gray-500 mt-1">Ative o quiz numa landing page para começar a receber leads.</p>
            </div>
          ) : (
            leads.map((lead) => {
              const deadline = sdrDeadline(lead);
              const waDigits = lead.whatsapp.replace(/\D/g, '');
              const isOpen = expanded === lead._id;
              const nurture = nurtureLabel(lead);
              return (
                <div key={lead._id} className={`bg-white rounded-lg shadow ${deadline?.overdue ? 'ring-1 ring-red-300' : ''}`}>
                  <div className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-gray-900">{lead.name}</span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full inline-flex items-center gap-1 ${OUTCOME_BADGE[lead.outcome].cls}`}>
                          {lead.outcome === 'vip' && <Star className="w-3 h-3" />}
                          {OUTCOME_BADGE[lead.outcome].label}
                        </span>
                        {deadline && (
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full inline-flex items-center gap-1 ${deadline.overdue ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                            <Clock className="w-3 h-3" /> {deadline.text}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        <span>{lead.whatsapp}</span>
                        <span className="truncate">{lead.email}</span>
                        <span>/l/{lead.landingPage?.slug || lead.landingPageSlugSnapshot}</span>
                        <span>{formatDateTime(lead.createdAt)}</span>
                        {nurture && <span className="text-blue-700">{nurture}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {waDigits && (
                        <a href={`https://wa.me/${waDigits}`} target="_blank" rel="noopener noreferrer" className="p-2 text-green-700 hover:bg-green-50 rounded-lg" title="Abrir WhatsApp">
                          <MessageCircle className="w-5 h-5" />
                        </a>
                      )}
                      <a href={`mailto:${lead.email}`} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg" title="Enviar e-mail">
                        <Mail className="w-5 h-5" />
                      </a>
                      <select
                        value={lead.status}
                        onChange={(e) => updateLead(lead, { status: e.target.value as LeadStatus })}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                        aria-label="Status do atendimento"
                      >
                        {(Object.keys(STATUS_LABELS) as LeadStatus[]).map((s) => (
                          <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : lead._id)}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                        aria-label={isOpen ? 'Recolher' : 'Ver respostas'}
                      >
                        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="border-t border-gray-100 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">Respostas</h4>
                        <dl className="space-y-1.5 text-sm">
                          {lead.answers.map((a) => (
                            <div key={a.questionKey}>
                              <dt className="text-gray-500">{a.questionText}</dt>
                              <dd className="font-medium text-gray-900">{a.optionLabel}</dd>
                            </div>
                          ))}
                        </dl>
                        {(lead.utm?.source || lead.utm?.campaign) && (
                          <p className="text-xs text-gray-500 mt-3">
                            Origem: {[lead.utm?.source, lead.utm?.campaign].filter(Boolean).join(' / ')}
                          </p>
                        )}
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">Notas do SDR</h4>
                        <textarea
                          rows={4}
                          value={notesDraft[lead._id] ?? lead.notes}
                          onChange={(e) => setNotesDraft((prev) => ({ ...prev, [lead._id]: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                          placeholder="Ex.: ligou dia 12, pediu proposta para família de 4"
                        />
                        <button
                          type="button"
                          disabled={(notesDraft[lead._id] ?? lead.notes) === lead.notes}
                          onClick={() => updateLead(lead, { notes: notesDraft[lead._id] ?? lead.notes })}
                          className="btn-primary text-sm px-4 py-1.5 mt-2 disabled:opacity-50"
                        >
                          Salvar notas
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLandingQuizLeads;
